export const ITS_ID_PATTERN = /^\d{8}$/;
const PHONE_PATTERN = /^\+?\d{7,15}$/;

export type StudentFormValues = {
  name?: string | null;
  itsId?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
  address?: string | null;
  notes?: string | null;
  joinedOn?: string | null;
  leftOn?: string | null;
  status?: string | null;
  guardian1Name?: string | null;
  guardian1Relation?: string | null;
  guardian1Phone?: string | null;
  guardian1ItsId?: string | null;
  guardian2Name?: string | null;
  guardian2Relation?: string | null;
  guardian2Phone?: string | null;
  guardian2ItsId?: string | null;
  primaryGuardian?: number | null;
  // Optional enrollment fields (for create)
  academicYearId?: string | null;
  classId?: string | null;
  section?: string | null;
  rollNo?: number | string | null;
  enrolledOn?: string | null;
  admissionMode?: "AUTO" | "NEW" | "RETURNING";
};

export type ItsLookupResult = {
  itsId: string;
  found: boolean;
  name: string | null;
  gender: "MALE" | "FEMALE" | null;
  address: string | null;
  fatherName?: string | null;
  mobile?: string | null;
  alreadyRegistered: { id: string; name: string; admissionNo: number } | null;
};

export type LookupNote = { severity: "info" | "success" | "warning"; message: string };

const blank = (v: unknown) => v == null || String(v).trim() === "";

/** Mirrors the API's phone rule (spaces, dashes, dots and brackets are ignored). */
export function isValidGuardianPhone(value: string | null | undefined): boolean {
  return PHONE_PATTERN.test((value ?? "").replace(/[\s\-().]/g, ""));
}

const FIELD_LABELS = {
  name: "name",
  gender: "gender",
  address: "address",
  guardian1Name: "guardian 1 name",
  guardian1Phone: "guardian 1 phone",
} as const;
type PrefillField = keyof typeof FIELD_LABELS;

/**
 * Fills only fields that are currently empty, so a directory lookup never silently overwrites
 * what the user typed. `kept` lists fields that had a value the directory could have replaced.
 */
export function applyItsPrefill(
  current: Partial<Record<PrefillField, string | null | undefined>>,
  lookup: ItsLookupResult
): {
  updates: Partial<Record<PrefillField, string>>;
  filled: PrefillField[];
  kept: PrefillField[];
} {
  const updates: Partial<Record<PrefillField, string>> = {};
  const filled: PrefillField[] = [];
  const kept: PrefillField[] = [];

  const lookupValues: Record<PrefillField, string | null | undefined> = {
    name: lookup.name,
    gender: lookup.gender,
    address: lookup.address,
    guardian1Name: lookup.fatherName,
    guardian1Phone: lookup.mobile,
  };

  for (const field of Object.keys(FIELD_LABELS) as PrefillField[]) {
    const incoming = lookupValues[field];
    if (blank(incoming)) continue;
    if (blank(current[field])) {
      updates[field] = incoming as string;
      filled.push(field);
    } else if (String(current[field]).trim() !== String(incoming).trim()) {
      kept.push(field);
    }
  }
  return { updates, filled, kept };
}

const list = (fields: PrefillField[]) => fields.map((f) => FIELD_LABELS[f]).join(", ");

/** User-facing summary of what a lookup did, so nothing changes without being stated. */
export function describeItsLookup(
  lookup: ItsLookupResult,
  outcome: { filled: PrefillField[]; kept: PrefillField[] },
  editingStudentId?: string
): LookupNote[] {
  const notes: LookupNote[] = [];
  const dup = lookup.alreadyRegistered;
  if (dup && dup.id !== editingStudentId) {
    notes.push({
      severity: "warning",
      message: `A student with this ITS already exists: ${dup.name} (admission no. ${dup.admissionNo}). It cannot be added twice.`,
    });
  }
  if (!lookup.found) {
    notes.push({
      severity: "info",
      message: "This ITS is not in the directory. That is fine — enter the details manually.",
    });
    return notes;
  }
  if (outcome.filled.length > 0) {
    notes.push({
      severity: "success",
      message: `Filled from the ITS directory: ${list(outcome.filled)}.`,
    });
  } else {
    notes.push({
      severity: "info",
      message: "Found in the ITS directory; nothing needed filling in.",
    });
  }
  if (outcome.kept.length > 0) {
    notes.push({
      severity: "info",
      message: `Kept what you entered for ${list(outcome.kept)}; the directory has a different value.`,
    });
  }
  return notes;
}

/** Client-side mirror of the API's cross-field rules; the API stays authoritative. */
export function validateStudentForm(values: StudentFormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  if (blank(values.name)) errors.name = "Required";
  if (blank(values.itsId)) errors.itsId = "Required";
  else if (!ITS_ID_PATTERN.test(String(values.itsId).trim()))
    errors.itsId = "Must be an 8-digit ITS number";

  if (
    !blank(values.leftOn) &&
    !blank(values.joinedOn) &&
    String(values.leftOn) < String(values.joinedOn)
  ) {
    errors.leftOn = "Cannot be before the joining date";
  }

  if (!blank(values.guardian1Phone) && !isValidGuardianPhone(values.guardian1Phone)) {
    errors.guardian1Phone = "7-15 digits, optional +";
  }
  if (!blank(values.guardian1ItsId) && !ITS_ID_PATTERN.test(String(values.guardian1ItsId).trim())) {
    errors.guardian1ItsId = "Must be an 8-digit ITS number";
  }

  const g2Filled = [values.guardian2Name, values.guardian2Relation, values.guardian2Phone].map(
    (v) => !blank(v)
  );
  const anyG2 = g2Filled.some(Boolean) || !blank(values.guardian2ItsId);
  if (anyG2) {
    if (!g2Filled[0]) errors.guardian2Name = "Required when adding guardian 2";
    if (!g2Filled[1]) errors.guardian2Relation = "Required when adding guardian 2";
    if (!g2Filled[2]) errors.guardian2Phone = "Required when adding guardian 2";
    else if (!isValidGuardianPhone(values.guardian2Phone))
      errors.guardian2Phone = "7-15 digits, optional +";
    if (
      !blank(values.guardian2ItsId) &&
      !ITS_ID_PATTERN.test(String(values.guardian2ItsId).trim())
    ) {
      errors.guardian2ItsId = "Must be an 8-digit ITS number";
    }
  }
  if (Number(values.primaryGuardian) === 2 && !g2Filled.every(Boolean)) {
    errors.primaryGuardian = "Fill in guardian 2 first, or choose guardian 1";
  }
  return errors;
}

/** Prepares payload for creating a student, optionally enrolling them if classId is provided. */
export function toStudentCreatePayload(values: StudentFormValues): Record<string, unknown> {
  const { admissionMode, ...rest } = values;
  const payload: Record<string, unknown> = { ...rest };

  if (payload.classId) {
    if (admissionMode === "NEW") {
      payload.isNewAdmission = true;
    } else if (admissionMode === "RETURNING") {
      payload.isNewAdmission = false;
    }
    if (payload.rollNo !== undefined && payload.rollNo !== null && payload.rollNo !== "") {
      payload.rollNo = Number(payload.rollNo);
    } else {
      payload.rollNo = null;
    }
  } else {
    delete payload.academicYearId;
    delete payload.classId;
    delete payload.section;
    delete payload.rollNo;
    delete payload.enrolledOn;
  }

  return payload;
}
